/**
 * VictoryManager System
 *
 * Phase 5: Manages victory detection and celebration sequence
 */

import Phaser from 'phaser';
import { VictoryPodium } from '../entities/VictoryPodium';
import { CelebrationEffects } from './CelebrationEffects';
import { GAME_CONSTANTS, GAME_EVENTS } from '../config/constants';
import type { GameScene } from '../scenes/GameScene';

export interface GameStats {
  completionTime: number;
  totalBounces: number;
  totalFalls: number;
  highestJump: number;
  attempts: number;
  collectibles: number; // Number of collectibles collected
  totalCollectibles: number; // Total collectibles in game
  devMode: boolean; // True if teleport/cheats were used
}

export class VictoryManager {
  private scene: Phaser.Scene;
  private victoryPodium: VictoryPodium;
  private victoryTriggered: boolean = false;
  private celebrationEffects: CelebrationEffects;

  constructor(scene: GameScene) {
    this.scene = scene;
    this.celebrationEffects = new CelebrationEffects(scene);

    // Create victory podium at the summit
    // CRITICAL: Y-flip conversion! Level data uses Y=0 at bottom, Phaser uses Y=0 at top
    // PODIUM_Y is in level coordinates (11825 = 400px above platform #104)
    // Convert to Phaser coordinates: gameY = WORLD_HEIGHT - levelY
    const WORLD_HEIGHT = 12725; // Match updated world height
    const podiumGameY = WORLD_HEIGHT - GAME_CONSTANTS.VICTORY.PODIUM_Y;

    this.victoryPodium = new VictoryPodium(
      scene,
      GAME_CONSTANTS.VICTORY.PODIUM_X,
      podiumGameY // Use converted Y coordinate
    );
  }

  public update(bloboPosition: { x: number; y: number }): void {
    if (this.victoryTriggered) return;

    // Check if Blobo has entered the victory trigger zone
    if (this.checkVictoryTrigger(bloboPosition)) {
      this.triggerVictory(bloboPosition);
    }
  }

  private checkVictoryTrigger(bloboPosition: { x: number; y: number }): boolean {
    const triggerZone = this.victoryPodium.getTriggerZone();

    // Check if Blobo's center is within the trigger zone bounds
    const triggerBounds = triggerZone.getBounds();

    return (
      bloboPosition.x >= triggerBounds.left &&
      bloboPosition.x <= triggerBounds.right &&
      bloboPosition.y >= triggerBounds.top &&
      bloboPosition.y <= triggerBounds.bottom
    );
  }

  private triggerVictory(bloboPosition: { x: number; y: number }): void {
    this.victoryTriggered = true;

    // Get completion time from scene
    const gameScene = this.scene as GameScene;
    const completionTime = gameScene.getElapsedTime();

    // Emit victory event to stop timer and disable input
    this.scene.events.emit(GAME_EVENTS.VICTORY_ACHIEVED);

    // Play celebration sequence
    this.celebrationEffects.playVictorySequence(bloboPosition.x, bloboPosition.y, completionTime);

    // Show victory UI after 1 second delay
    this.scene.time.delayedCall(1000, () => {
      this.showVictoryScene(completionTime);
    });
  }

  private showVictoryScene(completionTime: number): void {
    // Get game stats
    const gameScene = this.scene as GameScene;
    const stats = gameScene.getGameStats();

    // Launch victory scene as overlay
    this.scene.scene.launch('VictoryScene', {
      completionTime: completionTime,
      stats: stats,
    });
  }

  public destroy(): void {
    this.victoryPodium?.destroy();
    this.celebrationEffects?.destroy();
  }
}
